import { collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, setDoc, query, where, orderBy, limit, Timestamp, deleteField } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { secureEmailService } from './secureEmailService';
import type { CampaignEmailOptions } from './secureEmailService';
import { getEmailTemplate, processTemplate, EmailTemplate } from './emailTemplates';
import { sendingDomainService } from './sendingDomainService';
import { NotificationService } from './notificationService';
import { hasFeature } from '../config/planFeatures';
import { applyUnsubscribeUrlToHtml, buildUnsubscribeApiUrl } from '../utils/unsubscribeUrl';
import { formatDomainWarmingHelpMessage } from '../utils/domainWarming';
import { APP_WEB_ORIGIN, LANDING_WEB_ORIGIN } from '../config/domainConfig';
import { buildCampaignLinkRegistry } from '../utils/emailLinkRegistry';
import { campaignLinkService } from './campaignLinkService';
import { applyCampaignTrackingToHtml } from './secureEmailService';
import type { CampaignLinkRecord } from '../types/emailLinkAnalytics';

// Helper function to get sender email address
// Uses the original email if the domain is verified in SES, otherwise falls back to subdomain
const getSenderEmailAddress = async (userId: string, email: string, workspaceId?: string): Promise<string> => {
  try {
    return await sendingDomainService.getSendingAddressForEmail(userId, email, workspaceId);
  } catch (error) {
    console.warn('Error getting sender address, using subdomain fallback:', error);
    return convertToSubdomainEmail(email);
  }
};

// Fallback: Convert email to use scubamail.in domain for sending
const convertToSubdomainEmail = (email: string): string => {

  if (!email || !email.includes('@')) {
    return email;
  }

  const [localPart, domain] = email.split('@');

  // If email is already from scubamail.in, use it as-is
  if (domain === 'scubamail.io' || domain === 'send.scubamail.io') {
    return email;
  }

  // For other domains, use the local part with scubamail.in
  // This ensures emails are sent from verified scubamail.in domain
  // Sanitize local part to remove special characters that might cause issues
  const sanitizedLocalPart = localPart.replace(/[^a-zA-Z0-9._-]/g, '');
  return `${sanitizedLocalPart}@send.scubamail.io`;
};

export interface Campaign {
  id?: string;
  name: string;
  type?: 'email' | 'whatsapp'; // Defaults to 'email' if undefined
  subject: string;
  previewText?: string;
  htmlContent: string;
  textContent?: string;
  senderName: string;
  senderEmail: string;
  replyToEmail?: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled';
  recipientType: 'all' | 'segment' | 'list' | 'workspace' | 'tag';
  directEmails?: string[];
  recipientListId?: string;
  recipientSegmentId?: string;
  scheduledAt?: Timestamp;
  sentAt?: Timestamp;
  multiTimezoneSchedule?: {
    targetDate: string;
    targetTime: string;
    schedules: {
      timezone: string;
      scheduledAt: Timestamp;
    }[];
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
  userId: string;
  workspaceId: string;
  openTracking: boolean;
  clickTracking: boolean;
  googleAnalyticsTracking: boolean;
  personalizedToField: boolean;
  tags: { [key: string]: string };
  lastEditedIn?: 'setup' | 'html-editor' | 'design' | 'content';
  statistics: {
    totalRecipients: number;
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    complained: number;
    unsubscribed: number;
    read?: number; // For WhatsApp campaigns
  };
  templateId?: string;
  designJson?: any; // Store the full template structure from drag-and-drop editor for restoration
}

export interface CampaignRecipient {
  id: string;
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  customFields: { [key: string]: any };
  status: 'pending' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'complained' | 'unsubscribed';
  sentAt?: Timestamp;
  deliveredAt?: Timestamp;
  openedAt?: Timestamp;
  clickedAt?: Timestamp;
  bouncedAt?: Timestamp;
  complainedAt?: Timestamp;
  unsubscribedAt?: Timestamp;
  errorMessage?: string;
}

// Create a new campaign
export const createCampaign = async (campaign: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt' | 'statistics'>): Promise<string> => {
  try {
    console.log('createCampaign called with:', campaign);

    const campaignData = {
      ...campaign,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      statistics: {
        totalRecipients: 0,
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        bounced: 0,
        complained: 0,
        unsubscribed: 0,
      },
    };

    // Filter out undefined values as Firebase doesn't allow them
    const cleanedCampaignData = Object.fromEntries(
      Object.entries(campaignData).filter(([_, value]) => value !== undefined)
    );

    console.log('Campaign data to save to Firestore:', campaignData);
    console.log('Cleaned campaign data (undefined values removed):', cleanedCampaignData);
    console.log('Database instance:', db);
    console.log('Collection reference:', collection(db, 'campaigns'));

    const sanitizedCampaignData = sanitizeFirestoreData(cleanedCampaignData);
    console.log('Sanitized campaign data for Firestore:', sanitizedCampaignData);

    const docRef = await addDoc(collection(db, 'campaigns'), sanitizedCampaignData);
    console.log('Campaign created successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Detailed error in createCampaign:', {
      error,
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      errorCode: (error as any)?.code,
      errorStack: error instanceof Error ? error.stack : undefined,
      campaignData: campaign
    });
    throw new Error(`Failed to create campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Get all campaigns for a user in a specific workspace
export const getCampaigns = async (userId: string, workspaceId: string): Promise<Campaign[]> => {
  try {
    console.log('getCampaigns called with userId:', userId, 'workspaceId:', workspaceId);

    const q = query(
      collection(db, 'campaigns'),
      where('userId', '==', userId),
      where('workspaceId', '==', workspaceId)
    );

    console.log('Query created, executing...');
    const querySnapshot = await getDocs(q);
    console.log('Query executed, found', querySnapshot.docs.length, 'campaigns');

    const campaigns = await Promise.all(
      querySnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        const campaign = { id: docSnap.id, ...data } as Campaign;
        return reconcileCampaignStatus(campaign);
      })
    );

    // Sort by updatedAt in descending order (newest first)
    campaigns.sort((a, b) => {
      const aTime = a.updatedAt?.seconds || 0;
      const bTime = b.updatedAt?.seconds || 0;
      return bTime - aTime;
    });

    console.log('Mapped campaigns:', campaigns);
    return campaigns;
  } catch (error) {
    console.error('Error getting campaigns:', error);
    throw new Error('Failed to get campaigns');
  }
};

// Get a single campaign
export const getCampaign = async (campaignId: string): Promise<Campaign | null> => {
  try {
    console.log('Getting campaign with ID:', campaignId);
    const docRef = doc(db, 'campaigns', campaignId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const rawData = docSnap.data();
      // Convert objects back to nested arrays if needed
      const campaignData = {
        id: docSnap.id,
        ...convertObjectsToNestedArrays(rawData),
      } as Campaign;
      console.log('Campaign found:', campaignData);
      return reconcileCampaignStatus(campaignData);
    }
    console.log('Campaign not found for ID:', campaignId);
    return null;
  } catch (error) {
    console.error('Error getting campaign:', error);
    throw new Error('Failed to get campaign');
  }
};

// Update a campaign
// Convert nested arrays to objects for Firestore compatibility
const convertNestedArraysToObjects = (value: any): any => {
  if (value === null || value === undefined) {
    return value;
  }

  if (value instanceof Timestamp || value instanceof Date) {
    return value;
  }

  if (Array.isArray(value)) {
    // Check if this array contains arrays (nested arrays)
    const hasNestedArrays = value.some(item => Array.isArray(item));

    if (hasNestedArrays) {
      // Convert array of arrays to object with numeric keys
      const obj: Record<string, any> = {};
      value.forEach((item, index) => {
        obj[index.toString()] = convertNestedArraysToObjects(item);
      });
      return obj;
    }

    // Regular array - just sanitize items
    return value.map((item) => convertNestedArraysToObjects(item));
  }

  if (typeof value === 'object') {
    const sanitized: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entryValue]) => {
      if (entryValue === undefined) return;
      sanitized[key] = convertNestedArraysToObjects(entryValue);
    });
    return sanitized;
  }

  return value;
};

// Convert objects back to arrays when loading from Firestore
const convertObjectsToNestedArrays = (value: any): any => {
  if (value === null || value === undefined) {
    return value;
  }

  if (value instanceof Timestamp || value instanceof Date) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => convertObjectsToNestedArrays(item));
  }

  if (typeof value === 'object') {
    // Check if this looks like a converted array (has ONLY numeric string keys)
    const keys = Object.keys(value);
    const hasOnlyNumericKeys = keys.length > 0 && keys.every(key => /^\d+$/.test(key));

    if (hasOnlyNumericKeys) {
      // Convert back to array
      const maxIndex = Math.max(...keys.map(k => parseInt(k, 10)));
      const arr: any[] = [];
      for (let i = 0; i <= maxIndex; i++) {
        const key = i.toString();
        if (key in value) {
          arr[i] = convertObjectsToNestedArrays(value[key]);
        } else {
          arr[i] = undefined;
        }
      }
      return arr;
    }

    // Regular object - recursively convert
    const converted: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entryValue]) => {
      converted[key] = convertObjectsToNestedArrays(entryValue);
    });
    return converted;
  }

  return value;
};

const sanitizeFirestoreData = <T>(value: T): T => {
  return convertNestedArraysToObjects(value) as T;
};

export type CampaignUpdateOptions = {
  /** Explicit opt-in to revert sent/sending → draft (resend flow only). */
  reopenForSend?: boolean;
};

export const updateCampaign = async (
  campaignId: string,
  updates: Partial<Campaign> & CampaignUpdateOptions
): Promise<void> => {
  try {
    const reopenForSend = updates.reopenForSend === true;
    const { reopenForSend: _reopen, ...campaignUpdates } = updates;

    console.log('Updating campaign:', campaignId, 'with updates:', campaignUpdates);

    // Check if campaign exists and is not sent
    const campaign = await getCampaign(campaignId);
    if (!campaign) {
      throw new Error('Campaign not found');
    }

    // Allowed lifecycle transitions (do not block send completion or failure recovery).
    const isResendRevert =
      reopenForSend &&
      (campaignUpdates.status === 'draft' || campaignUpdates.status === 'sending');
    const isCompletingSend =
      campaign.status === 'sending' && campaignUpdates.status === 'sent';
    const isSendFailureRevert =
      campaign.status === 'sending' && campaignUpdates.status === 'draft';
    const isSentStatsRefresh =
      campaign.status === 'sent' && campaignUpdates.status === 'sent';

    if (
      (campaign.status === 'sent' || campaign.status === 'sending') &&
      !isResendRevert &&
      !isCompletingSend &&
      !isSendFailureRevert &&
      !isSentStatsRefresh
    ) {
      throw new Error('Cannot edit sent campaigns. This campaign has already been delivered.');
    }

    const docRef = doc(db, 'campaigns', campaignId);
    const sanitizedUpdates = sanitizeFirestoreData(campaignUpdates);

    // Draft campaigns must not keep a stale sentAt — unless stats prove it was already sent (repair path).
    const nextStatus = campaignUpdates.status ?? campaign.status;
    const hasSentStats = Number(campaign.statistics?.sent ?? 0) > 0;
    if (nextStatus === 'draft' && campaign.sentAt && !hasSentStats) {
      (sanitizedUpdates as Record<string, unknown>).sentAt = deleteField();
    }

    await updateDoc(docRef, {
      ...sanitizedUpdates,
      updatedAt: Timestamp.now(),
    });
    console.log('Campaign updated successfully');
  } catch (error) {
    console.error('Error updating campaign:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to update campaign');
  }
};

// Interface for deleted campaigns
export interface DeletedCampaign {
  id: string;
  campaignData: Campaign;
  deletedAt: Timestamp;
  deletedBy: string;
  workspaceId: string;
  canRestore: boolean;
}

// Get deleted campaigns for a workspace (only rows the current user may read per Firestore rules)
export const getDeletedCampaigns = async (workspaceId: string, deletedByUserId: string): Promise<DeletedCampaign[]> => {
  try {
    if (!deletedByUserId) {
      return [];
    }
    console.log('getDeletedCampaigns called with workspaceId:', workspaceId, 'deletedByUserId:', deletedByUserId);

    // First try with orderBy (requires composite index)
    let q;
    try {
      q = query(
        collection(db, 'deleted_campaigns'),
        where('workspaceId', '==', workspaceId),
        where('canRestore', '==', true),
        where('deletedBy', '==', deletedByUserId),
        orderBy('deletedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const deletedCampaigns = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as DeletedCampaign[];

      console.log('Found deleted campaigns:', deletedCampaigns.length);
      return deletedCampaigns;
    } catch (orderByError: any) {
      // If orderBy fails (likely due to missing index), try without it
      if (orderByError?.code === 'failed-precondition' || orderByError?.message?.includes('index')) {
        console.warn('Composite index missing, querying without orderBy:', orderByError);
        q = query(
          collection(db, 'deleted_campaigns'),
          where('workspaceId', '==', workspaceId),
          where('canRestore', '==', true),
          where('deletedBy', '==', deletedByUserId)
        );
        const querySnapshot = await getDocs(q);
        const deletedCampaigns = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as DeletedCampaign[];

        // Sort manually by deletedAt
        deletedCampaigns.sort((a, b) => {
          const aTime = a.deletedAt?.seconds || 0;
          const bTime = b.deletedAt?.seconds || 0;
          return bTime - aTime;
        });

        console.log('Found deleted campaigns (without orderBy):', deletedCampaigns.length);
        return deletedCampaigns;
      }
      throw orderByError;
    }
  } catch (error) {
    console.error('Error getting deleted campaigns:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : String(error),
      code: (error as any)?.code,
      stack: error instanceof Error ? error.stack : undefined
    });
    throw new Error(`Failed to get deleted campaigns: ${error instanceof Error ? error.message : String(error)}`);
  }
};

// Restore a deleted campaign (deletedByUserId scopes fallback queries to rule-safe filters)
export const restoreCampaign = async (
  deletedCampaignId: string,
  originalCampaignId: string,
  deletedByUserId: string
): Promise<void> => {
  try {
    console.log('restoreCampaign called:', { deletedCampaignId, originalCampaignId });

    // Try to find the document - it might have been filtered out or the ID might be wrong
    // First, try direct lookup
    const docRef = doc(db, 'deleted_campaigns', deletedCampaignId);
    console.log('Fetching document from path:', docRef.path);

    let deletedCampaignDoc = await getDoc(docRef);
    console.log('Document exists (direct lookup):', deletedCampaignDoc.exists());

    // If not found, try to find it by querying with the original campaign ID stored in the data
    if (!deletedCampaignDoc.exists() && originalCampaignId && deletedByUserId) {
      console.log('Document not found with direct ID, trying to find by original campaign ID:', originalCampaignId);
      const q = query(
        collection(db, 'deleted_campaigns'),
        where('id', '==', originalCampaignId),
        where('canRestore', '==', true),
        where('deletedBy', '==', deletedByUserId),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        deletedCampaignDoc = querySnapshot.docs[0];
        console.log('Found document by querying with original campaign ID');
      }
    }

    if (!deletedCampaignDoc.exists()) {
      console.error('Document not found. Tried:', {
        deletedCampaignId,
        originalCampaignId,
        documentPath: docRef.path
      });
      throw new Error(`Deleted campaign not found with ID: ${deletedCampaignId}. It may have been permanently deleted.`);
    }

    const deletedCampaignData = deletedCampaignDoc.data();
    console.log('Retrieved deleted campaign data:', {
      documentId: deletedCampaignDoc.id,
      dataKeys: Object.keys(deletedCampaignData || {}),
      hasCampaignData: !!(deletedCampaignData as any)?.campaignData,
      storedId: (deletedCampaignData as any)?.id,
      originalCampaignId: (deletedCampaignData as any)?.campaignData?.id
    });

    const deletedCampaign = {
      id: deletedCampaignDoc.id,
      ...deletedCampaignData
    } as DeletedCampaign;

    // Check if it's been more than 30 days
    const deletedAt = deletedCampaign.deletedAt.toDate();
    const daysSinceDeletion = (Date.now() - deletedAt.getTime()) / (1000 * 60 * 60 * 24);

    if (daysSinceDeletion > 30) {
      throw new Error('Cannot restore campaign. It has been permanently deleted after 30 days.');
    }

    // Use the original campaign ID from the stored data, or fallback to the provided one
    const campaignIdToRestore = deletedCampaign.campaignData?.id || deletedCampaign.id || originalCampaignId;
    console.log('Restoring campaign with ID:', campaignIdToRestore);

    if (!campaignIdToRestore) {
      throw new Error('Cannot determine campaign ID to restore');
    }

    // Restore the campaign to campaigns collection
    const campaignData = {
      ...deletedCampaign.campaignData,
      id: campaignIdToRestore, // Ensure ID is set
      updatedAt: Timestamp.now(),
    };

    // Remove undefined values and the id field (Firestore doesn't store it as a field)
    const { id: _, ...campaignDataWithoutId } = campaignData;
    const cleanedCampaignData = Object.fromEntries(
      Object.entries(campaignDataWithoutId).filter(([_, value]) => value !== undefined)
    );

    console.log('Restoring campaign data to campaigns collection:', {
      campaignId: campaignIdToRestore,
      campaignName: campaignData.name
    });

    const sanitizedToRestore = sanitizeFirestoreData(cleanedCampaignData);
    console.log('Sanitized restoration data for Firestore:', sanitizedToRestore);

    await setDoc(doc(db, 'campaigns', campaignIdToRestore), sanitizedToRestore);
    console.log('Campaign restored to campaigns collection');

    // Mark as permanently deleted in deleted_campaigns (or delete the record)
    // Use the actual document ID we found (might be different from deletedCampaignId if we used fallback)
    const actualDocId = deletedCampaignDoc.id;
    console.log('Marking document as cannot restore:', actualDocId);
    await updateDoc(doc(db, 'deleted_campaigns', actualDocId), {
      canRestore: false,
    });
    console.log('Document marked as cannot restore');
  } catch (error) {
    console.error('Error restoring campaign:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to restore campaign');
  }
};

// Permanently delete a campaign from deleted_campaigns (deletedByUserId scopes fallback query to rule-safe filters)
export const permanentlyDeleteCampaign = async (deletedCampaignId: string, deletedByUserId?: string): Promise<void> => {
  try {
    console.log('permanentlyDeleteCampaign called with ID:', deletedCampaignId);

    // Try direct lookup first
    const docRef = doc(db, 'deleted_campaigns', deletedCampaignId);
    console.log('Fetching document from path:', docRef.path);

    let deletedCampaignDoc = await getDoc(docRef);
    console.log('Document exists (direct lookup):', deletedCampaignDoc.exists());

    // If not found, try to find it by querying with the stored ID field
    if (!deletedCampaignDoc.exists() && deletedByUserId) {
      console.log('Document not found with direct ID, trying to find by stored ID field:', deletedCampaignId);
      const q = query(
        collection(db, 'deleted_campaigns'),
        where('id', '==', deletedCampaignId),
        where('deletedBy', '==', deletedByUserId),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        deletedCampaignDoc = querySnapshot.docs[0];
        console.log('Found document by querying with stored ID:', deletedCampaignDoc.id);
      }
    }

    if (!deletedCampaignDoc.exists()) {
      console.error('Document not found. Tried:', {
        deletedCampaignId,
        documentPath: docRef.path
      });
      throw new Error(`Deleted campaign not found with ID: ${deletedCampaignId}. It may have already been deleted.`);
    }

    // Use the actual document ID we found
    const actualDocId = deletedCampaignDoc.id;
    console.log('Deleting document with ID:', actualDocId);
    await deleteDoc(doc(db, 'deleted_campaigns', actualDocId));
    console.log('Campaign permanently deleted from Firestore');
  } catch (error) {
    console.error('Error permanently deleting campaign:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : String(error),
      code: (error as any)?.code,
      stack: error instanceof Error ? error.stack : undefined
    });
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to permanently delete campaign');
  }
};

// Delete a campaign (soft delete - moves to deleted_campaigns collection)
export const deleteCampaign = async (campaignId: string, userId: string): Promise<void> => {
  try {
    if (!campaignId || !userId) {
      throw new Error('Campaign ID and user ID are required');
    }

    // Check if campaign exists
    const campaign = await getCampaign(campaignId);
    if (!campaign) {
      // Campaign might already be deleted - check if it exists in deleted_campaigns
      const deletedQuery = query(
        collection(db, 'deleted_campaigns'),
        where('id', '==', campaignId),
        where('deletedBy', '==', userId),
        limit(1)
      );
      const deletedSnapshot = await getDocs(deletedQuery);

      if (!deletedSnapshot.empty) {
        // Campaign is already in deleted_campaigns, consider it a success
        console.log('Campaign already deleted, skipping');
        return;
      }

      throw new Error('Campaign not found');
    }

    if (campaign.status === 'sent') {
      throw new Error('Cannot delete sent campaigns. This campaign has already been delivered.');
    }

    // Check if campaign is already in deleted_campaigns (prevent duplicate deletion)
    const existingDeletedQuery = query(
      collection(db, 'deleted_campaigns'),
      where('id', '==', campaignId),
      where('deletedBy', '==', userId),
      limit(1)
    );
    const existingDeletedSnapshot = await getDocs(existingDeletedQuery);

    if (!existingDeletedSnapshot.empty) {
      // Campaign is already in deleted_campaigns, just delete from campaigns collection
      console.log('Campaign already in deleted_campaigns, removing from campaigns collection');
      const docRef = doc(db, 'campaigns', campaignId);
      await deleteDoc(docRef);
      return;
    }

    // Sanitize campaign data to convert nested arrays to objects for Firestore compatibility.
    // Ensure userId matches the actor so deleted_campaigns create rules always see consistent campaignData.userId.
    const sanitizedCampaignData = sanitizeFirestoreData({ ...campaign, userId });

    // Save to deleted_campaigns collection before deleting
    const deletedCampaignData = {
      id: campaignId,
      campaignData: sanitizedCampaignData,
      deletedAt: Timestamp.now(),
      deletedBy: userId,
      workspaceId: campaign.workspaceId,
      canRestore: true,
    };

    console.log('Saving deleted campaign to deleted_campaigns:', {
      campaignId,
      workspaceId: campaign.workspaceId,
      campaignName: campaign.name,
      deletedCampaignData
    });

    // Remove undefined values before saving
    const cleanedDeletedCampaignData = Object.fromEntries(
      Object.entries(deletedCampaignData).filter(([_, value]) => value !== undefined)
    );

    const deletedDocRef = await addDoc(collection(db, 'deleted_campaigns'), cleanedDeletedCampaignData);
    console.log('Deleted campaign saved with ID:', deletedDocRef.id);

    // Delete from campaigns collection
    const docRef = doc(db, 'campaigns', campaignId);
    await deleteDoc(docRef);
    console.log('Campaign deleted successfully from campaigns collection');
  } catch (error) {
    console.error('Error deleting campaign:', error);
    console.error('Error details:', {
      campaignId,
      userId,
      message: error instanceof Error ? error.message : String(error),
      code: (error as any)?.code,
      stack: error instanceof Error ? error.stack : undefined
    });

    if (error instanceof Error) {
      // Re-throw specific errors
      if (error.message.includes('not found') || error.message.includes('Campaign not found')) {
        throw new Error('Campaign not found. It may have already been deleted.');
      }
      if (error.message.includes('sent campaigns') || error.message.includes('already been delivered')) {
        throw error; // Re-throw sent campaign error as-is
      }
      throw error;
    }
    throw new Error('Failed to delete campaign');
  }
};

/** Mark campaign sent after delivery succeeds (shared by email + WhatsApp paths). */
async function markCampaignAsSent(
  campaignId: string,
  recipientCount: number,
  successCount: number
): Promise<void> {
  const docRef = doc(db, 'campaigns', campaignId);
  await updateDoc(
    docRef,
    sanitizeFirestoreData({
      status: 'sent',
      sentAt: Timestamp.now(),
      statistics: {
        totalRecipients: recipientCount,
        sent: successCount,
        delivered: successCount,
        opened: 0,
        clicked: 0,
        bounced: 0,
        complained: 0,
        unsubscribed: 0,
      },
      updatedAt: Timestamp.now(),
    })
  );
}

/** Best-effort recipient log — must not fail the send if emails already went out. */
async function persistCampaignRecipients(
  campaignId: string,
  recipients: CampaignRecipient[],
  sendResults: Array<{ success: boolean; error?: string }>,
  meta?: { userId: string; workspaceId: string }
): Promise<void> {
  try {
    const campaignRecipients = recipients.map((recipient, index) => {
      const result = sendResults[index];
      const recipientData: Record<string, unknown> = {
        campaignId,
        email: recipient.email,
        firstName: recipient.firstName,
        lastName: recipient.lastName,
        customFields: recipient.customFields,
        status: result?.success ? 'sent' : 'bounced',
        createdAt: Timestamp.now(),
      };
      if (meta?.userId) recipientData.userId = meta.userId;
      if (meta?.workspaceId) recipientData.workspaceId = meta.workspaceId;
      if (result?.success) {
        recipientData.sentAt = Timestamp.now();
      } else {
        recipientData.bouncedAt = Timestamp.now();
        recipientData.errorMessage = result?.error;
      }
      return recipientData;
    });
    await addCampaignRecipients(campaignId, campaignRecipients as Omit<CampaignRecipient, 'id'>[]);
  } catch (err) {
    console.error('Failed to persist campaign recipients (emails were already sent):', err);
  }
}

/**
 * Repair campaigns stuck as draft after a successful send (status reverted by error handler).
 */
export const reconcileCampaignStatus = async (campaign: Campaign): Promise<Campaign> => {
  if (!campaign.id) return campaign;
  const sentCount = Number(campaign.statistics?.sent ?? 0);
  if (campaign.status !== 'draft' || sentCount <= 0) {
    return campaign;
  }
  try {
    const sentAt = campaign.sentAt ?? Timestamp.now();
    await updateDoc(doc(db, 'campaigns', campaign.id), {
      status: 'sent',
      sentAt,
      updatedAt: Timestamp.now(),
    });
    console.log(`Repaired campaign status: ${campaign.id} draft → sent (${sentCount} sent)`);
    return { ...campaign, status: 'sent', sentAt };
  } catch (err) {
    console.warn('Failed to reconcile campaign status:', err);
    return campaign;
  }
};

// Send a campaign
export interface SendCampaignResult {
  success: boolean;
  messageId?: string;
  error?: string;
  results?: { results: any[]; successCount: number; failureCount: number };
  /** True when the send was handed to the server-side queue rather than completed inline. */
  queued?: boolean;
  /** Audience size resolved server-side. Only set on the queued path. */
  totalRecipients?: number;
  /** True when a send for this campaign was already in flight. */
  alreadyRunning?: boolean;
}

/**
 * Rollout switch for the server-side send queue.
 *
 * Defaults to ON. Set REACT_APP_USE_CAMPAIGN_QUEUE=false to fall back to the
 * legacy in-browser path (`sendCampaignLegacy`) without a code change — useful
 * if something unexpected shows up in production sends.
 */
const USE_CAMPAIGN_QUEUE = process.env.REACT_APP_USE_CAMPAIGN_QUEUE !== 'false';

/**
 * Start a campaign send.
 *
 * On the queued path the browser sends only a campaign ID. The server resolves
 * the audience, renders each email, and fans the work out across shard workers.
 * That removes three hard ceilings the old path kept hitting: the 10MB callable
 * payload limit, the 60s function timeout, and browser memory pressure from
 * holding every rendered body at once.
 *
 * Note the contract change: this now returns once the work is *accepted*, not
 * once every email is delivered. `results.successCount` is therefore 0 on the
 * queued path — poll `getCampaignSendProgress` for live counts, or read the
 * campaign's `statistics` once its status flips to `sent`.
 *
 * `recipients` is ignored when the queue is enabled; it is retained so existing
 * call sites keep compiling, and is still used by the legacy path.
 */
export const sendCampaign = async (
  campaignId: string,
  recipients: CampaignRecipient[]
): Promise<SendCampaignResult> => {
  if (!USE_CAMPAIGN_QUEUE) {
    return sendCampaignLegacy(campaignId, recipients);
  }

  try {
    const campaign = await getCampaign(campaignId);
    if (!campaign) {
      throw new Error('Campaign not found');
    }

    // WhatsApp still goes through its own callable — it is not queue-backed.
    if (campaign.type === 'whatsapp') {
      return sendCampaignLegacy(campaignId, recipients);
    }

    const enqueue = httpsCallable(functions, 'enqueueCampaignSend');
    const response: any = await enqueue({ campaignId });
    const data = response?.data || {};

    console.log('Campaign send enqueued:', data);

    NotificationService.sendToUser(
      campaign.userId,
      data.alreadyRunning ? 'Campaign send already in progress' : 'Campaign send started',
      data.alreadyRunning
        ? `A send for "${campaign.name || campaign.subject}" is already running.`
        : `Your campaign "${campaign.name || campaign.subject}" is being sent to ${Number(
            data.totalRecipients || 0
          ).toLocaleString()} recipient${data.totalRecipients === 1 ? '' : 's'}. We'll update the status as it completes.`,
      'info',
      'system',
      'Scubamail'
    ).catch((err) => console.error('Failed to send campaign queued notification:', err));

    return {
      success: true,
      queued: true,
      totalRecipients: Number(data.totalRecipients || 0),
      alreadyRunning: !!data.alreadyRunning,
      messageId: data.jobId,
      results: { results: [], successCount: 0, failureCount: 0 },
    };
  } catch (error: any) {
    console.error('Error enqueueing campaign send:', error);

    const errorMessage =
      error?.details?.errors?.join('; ') ||
      error?.message ||
      'Failed to start campaign send';

    // The server resets the campaign to draft when planning fails, so there is
    // no stuck `sending` state to clean up here.
    return {
      success: false,
      error: formatDomainWarmingHelpMessage(errorMessage),
    };
  }
};

/**
 * Legacy in-browser send path.
 *
 * Kept for rollback via REACT_APP_USE_CAMPAIGN_QUEUE=false and still used for
 * WhatsApp campaigns. Known limitations, which are precisely why the queued
 * path exists: it renders every recipient's body in the tab, ships the whole
 * batch in one callable, and has no per-recipient idempotency — a retry after a
 * timeout either double-sends or silently drops the remainder.
 */
export const sendCampaignLegacy = async (
  campaignId: string,
  recipients: CampaignRecipient[]
): Promise<SendCampaignResult> => {
  try {
    const campaign = await getCampaign(campaignId);
    if (!campaign) {
      throw new Error('Campaign not found');
    }

    if (campaign.status !== 'draft' && campaign.status !== 'sending') {
      throw new Error('Campaign can only be sent from draft status');
    }

    // Recover stuck "sending" from a prior attempt that delivered but failed to mark sent.
    if (campaign.status === 'sending' && Number(campaign.statistics?.sent ?? 0) > 0) {
      await markCampaignAsSent(
        campaignId,
        Number(campaign.statistics.totalRecipients ?? campaign.statistics.sent),
        Number(campaign.statistics.sent)
      );
      return {
        success: true,
        messageId: 'already-sent',
      };
    }

    // Update campaign status to sending
    await updateCampaign(campaignId, { status: 'sending' });

    // Check for WhatsApp campaign type
    console.log('🔍 Campaign type check:', {
      id: campaign.id,
      type: campaign.type,
      isWhatsapp: campaign.type === 'whatsapp'
    });

    if (campaign.type === 'whatsapp') {
      console.log('🚀 Sending WhatsApp campaign via Cloud Function');

      const sendWhatsAppCampaign = httpsCallable(functions, 'sendWhatsAppCampaign');

      const result: any = await sendWhatsAppCampaign({
        campaignId: campaignId,
        recipients: recipients.map(r => ({
          ...r,
          id: r.id, // Explicitly pass ID
          phone: r.phone || '',
        })),
        workspaceId: campaign.workspaceId
      });

      console.log('WhatsApp sending result:', result.data);

      if (result.data.failed > 0 && result.data.success === 0) {
        throw new Error('All WhatsApp messages failed to send');
      }

      const successCount = Number(result.data.success ?? 0);
      await markCampaignAsSent(campaignId, recipients.length, successCount);

      return {
        success: true,
        results: {
          successCount,
          failureCount: Number(result.data.failed ?? 0),
          results: result.data.results,
        },
      };
    }

    // Load template if needed
    let template: EmailTemplate | null = null;
    if (campaign.templateId) {
      try {
        template = await getEmailTemplate(campaign.templateId);
      } catch (error) {
        console.error('Error loading template:', error);
      }
    }

    // Fetch workspace data for address attachment
    let workspaceAddressString = '';
    try {
      const workspaceDoc = await getDoc(doc(db, 'workspaces', campaign.workspaceId));
      if (workspaceDoc.exists()) {
        const workspaceData = workspaceDoc.data();
        const address = workspaceData.settings?.address;
        if (address && !address.keepPrivate) {
          const parts = [
            address.addressLine1,
            address.addressLine2,
            address.city,
            address.region,
            address.postalCode,
            address.country
          ].filter(Boolean);
          workspaceAddressString = parts.join(', ');
        }
      }
    } catch (error) {
      console.error('Error fetching workspace address:', error);
    }

    // Define the mandatory address footer HTML
    const addressFooterHtml = workspaceAddressString ? `
      <div style="font-family: Arial, sans-serif; font-size: 11px; color: #9CA3AF; text-align: center; margin-top: 32px; padding-top: 16px; border-top: 1px solid #F3F4F6;">
        <p style="margin: 0 0 4px 0;">${workspaceAddressString}</p>
        <p style="margin: 0;">You received this email because you are subscribed to communications from ${campaign.senderName}.</p>
        <p style="margin: 8px 0 0 0;">
          <a href="{{unsubscribe_url}}" style="color: #9CA3AF; text-decoration: underline;">Unsubscribe from these emails</a>
        </p>
      </div>` : '';

    // Resolve the sender email address (uses verified domain if available)
    const resolvedSenderEmail = await getSenderEmailAddress(
      campaign.userId,
      campaign.senderEmail,
      campaign.workspaceId
    );
    console.log('📧 Sender email resolution:', {
      original: campaign.senderEmail,
      resolved: resolvedSenderEmail,
      usedVerifiedDomain: campaign.senderEmail === resolvedSenderEmail
    });

    // Check user plan for branding injection
    const { planService } = await import('./planService');
    const userPlanId = await planService.getUserPlan(campaign.userId);
    const canRemoveBadge = hasFeature(userPlanId, 'removeScubamailBadge');

    // Define branding HTML once
    const scubamailBrandingHtml = `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top: 8px;">
        <tr>
          <td align="center" style="padding: 12px 20px; font-family: Arial, sans-serif; font-size: 11px; color: #9CA3AF;">
            <a href="${LANDING_WEB_ORIGIN}" target="_blank" style="color: #9CA3AF; text-decoration: none;">Powered by Scubamail</a>
          </td>
        </tr>
      </table>`;

    // Build per-link registry once per campaign send (Phase 1 link analytics)
    let campaignLinkRegistry: CampaignLinkRecord[] = [];
    if (campaign.clickTracking !== false) {
      try {
        campaignLinkRegistry = buildCampaignLinkRegistry({
          designJson: campaign.designJson,
          htmlContent: campaign.htmlContent,
          workspaceId: campaign.workspaceId,
          campaignId,
          campaignName: campaign.name,
        });
        await campaignLinkService.saveCampaignLinks(campaignLinkRegistry);
        console.log(`📎 Registered ${campaignLinkRegistry.length} trackable links for campaign ${campaignId}`);
      } catch (linkRegistryError) {
        console.error('Failed to save campaign link registry (continuing send):', linkRegistryError);
      }
    }

    // Prepare email options for each recipient
    const emailOptions: CampaignEmailOptions[] = recipients.map(recipient => {
      // Prepare variables for template processing
      const variables = {
        first_name: recipient.firstName || '',
        last_name: recipient.lastName || '',
        email: recipient.email,
        ...recipient.customFields,
        unsubscribe_url: buildUnsubscribeApiUrl({
          email: recipient.email,
          userId: campaign.userId,
          workspaceId: campaign.workspaceId,
          campaignId,
        }),
        website_url: 'https://your-website.com', // This should come from user settings
        sender_address: workspaceAddressString,
      };

      // Process template if using one
      let processedSubject = campaign.subject;
      let processedHtml = campaign.htmlContent;
      let processedText = campaign.textContent;

      if (template) {
        const processed = processTemplate(template, variables);
        processedSubject = processed.subject;
        processedHtml = processed.htmlContent;
        processedText = processed.textContent;
      } else {
        Object.entries(variables).forEach(([key, value]) => {
          const regex = new RegExp(`{{${key}}}`, 'g');
          processedSubject = processedSubject.replace(regex, value || '');
          processedHtml = processedHtml.replace(regex, value || '');
          if (processedText) {
            processedText = processedText.replace(regex, value || '');
          }
        });
      }

      processedHtml = applyUnsubscribeUrlToHtml(processedHtml, variables.unsubscribe_url);
      if (processedText) {
        processedText = applyUnsubscribeUrlToHtml(processedText, variables.unsubscribe_url);
      }

      // Inject mandatory address footer if it exists and not already present
      if (addressFooterHtml && !processedHtml.includes(workspaceAddressString) && !processedHtml.includes('Unsubscribe from these emails')) {
        // Replace variable in footer
        const finalFooter = addressFooterHtml.replace('{{unsubscribe_url}}', variables.unsubscribe_url);
        
        if (processedHtml.includes('</body>')) {
          processedHtml = processedHtml.replace('</body>', `${finalFooter}</body>`);
        } else {
          processedHtml += finalFooter;
        }
      }

      // Send-time branding injection for free plan users
      // This is the tamper-proof enforcement layer - even if branding was removed from the canvas,
      // it gets re-injected at send time for users without the removeScubamailBadge feature
      if (!canRemoveBadge) {
        // Check if branding already exists in the HTML (from the canvas block)
        if (!processedHtml.includes('Powered by Scubamail')) {
          // Inject before </body> if exists, otherwise append to the end
          if (processedHtml.includes('</body>')) {
            processedHtml = processedHtml.replace('</body>', `${scubamailBrandingHtml}</body>`);
          } else {
            processedHtml += scubamailBrandingHtml;
          }
        }
      }

      // Apply open/click tracking (bulk send path does not wrap links server-side)
      processedHtml = applyCampaignTrackingToHtml(processedHtml, {
        trackingEnabled: true,
        openTracking: campaign.openTracking,
        clickTracking: campaign.clickTracking !== false,
        campaignId,
        workspaceId: campaign.workspaceId,
        recipientEmail: recipient.email,
        recipientId: recipient.id,
        linkRegistry: campaignLinkRegistry,
      });

      return {
        from: {
          email: resolvedSenderEmail,
          name: campaign.senderName,
        },
        to: [{
          email: recipient.email,
          name: recipient.firstName ? `${recipient.firstName} ${recipient.lastName || ''}`.trim() : undefined,
        }],
        subject: processedSubject,
        htmlBody: processedHtml,
        textBody: processedText,
        replyTo: campaign.replyToEmail,
        campaignId: campaignId,
        trackingEnabled: true,
        openTracking: campaign.openTracking,
        clickTracking: campaign.clickTracking,
        linkRegistry: campaignLinkRegistry,
        tags: {
          ...campaign.tags,
          RecipientId: recipient.id,
        },
        userId: campaign.userId,
        workspaceId: campaign.workspaceId,
        unsubscribeUrl: variables.unsubscribe_url,
      };
    });


    // Send emails in bulk using secure service
    console.log('Sending emails in bulk via secure service...', {
      emailCount: emailOptions.length,
      campaignId: campaignId,
      firstEmail: emailOptions[0] ? {
        to: emailOptions[0].to,
        subject: emailOptions[0].subject,
        hasHtmlBody: !!emailOptions[0].htmlBody
      } : null
    });

    const results = await secureEmailService.sendBulkEmails(emailOptions);
    console.log('Email sending results:', {
      successCount: results.successCount,
      failureCount: results.failureCount,
      totalResults: results.results.length,
      firstResult: results.results[0]
    });

    // Check if all emails failed
    if (results.successCount === 0 && emailOptions.length > 0) {
      const firstError = results.results.find(r => !r.success);
      const errorMessage = firstError?.error || 'All emails failed to send';
      console.error('All emails failed to send:', errorMessage);
      throw new Error(errorMessage);
    }

    // Mark sent before optional recipient logging so a logging failure cannot revert status.
    console.log('Updating campaign statistics...');
    await markCampaignAsSent(campaignId, recipients.length, results.successCount);
    console.log('Campaign marked as sent');

    NotificationService.sendToUser(
      campaign.userId,
      'Campaign sent successfully',
      `Your campaign "${campaign.name || campaign.subject}" was sent to ${results.successCount.toLocaleString()} recipient${results.successCount === 1 ? '' : 's'}.${results.failureCount > 0 ? ` ${results.failureCount} failed.` : ''}`,
      results.failureCount > 0 ? 'warning' : 'success',
      'system',
      'Scubamail'
    ).catch(err => console.error('Failed to send campaign success notification:', err));

    await persistCampaignRecipients(campaignId, recipients, results.results, {
      userId: campaign.userId,
      workspaceId: campaign.workspaceId,
    });

    return {
      success: true,
      results,
    };
  } catch (error) {
    console.error('Error sending campaign:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      campaignId: campaignId
    });

    // Fire failure notification (non-blocking)
    try {
      const failedCampaign = await getCampaign(campaignId);
      if (failedCampaign) {
        NotificationService.sendToUser(
          failedCampaign.userId,
          'Campaign failed to send',
          `Your campaign "${failedCampaign.name || failedCampaign.subject}" could not be sent. ${error instanceof Error ? error.message : 'Please try again.'}`,
          'error',
          'system',
          'Scubamail'
        ).catch(err => console.error('Failed to send campaign failure notification:', err));
      }
    } catch (notifErr) {
      console.error('Failed to send campaign failure notification:', notifErr);
    }

    // Only revert to draft if send never completed — never overwrite a successful 'sent' status.
    try {
      const failedCampaign = await getCampaign(campaignId);
      if (failedCampaign?.status === 'sending') {
        await updateDoc(doc(db, 'campaigns', campaignId), {
          status: 'draft',
          updatedAt: Timestamp.now(),
        });
      }
    } catch (updateError) {
      console.error('Failed to update campaign status after error:', updateError);
    }

    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    // Provide more helpful error messages
    if (errorMessage.includes('not found') || errorMessage.includes('not-found')) {
      return {
        success: false,
        error: 'Email service is not configured. Please deploy Firebase Functions first.',
      };
    } else if (errorMessage.includes('unauthenticated')) {
      return {
        success: false,
        error: 'Authentication required. Please log in and try again.',
      };
    } else if (errorMessage.includes('permission')) {
      return {
        success: false,
        error: 'Permission denied. Please check your account permissions.',
      };
    } else if (errorMessage.includes('subscription') || errorMessage.includes('No active subscription')) {
      return {
        success: false,
        error: 'An active subscription is required to send emails. Please upgrade your plan to continue sending campaigns.',
      };
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Schedule a campaign
export interface CampaignSendProgress {
  exists: boolean;
  status?: 'planning' | 'running' | 'completed' | 'failed';
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  shardCount: number;
  shardsCompleted: number;
  deadLetterCount: number;
  error?: string | null;
}

/**
 * Live progress for a queued send.
 *
 * Needed because `sendCampaign` now returns as soon as the work is accepted; the
 * UI polls this to show progress instead of getting a synchronous result.
 */
export const getCampaignSendProgress = async (campaignId: string): Promise<CampaignSendProgress> => {
  try {
    const fn = httpsCallable(functions, 'getCampaignSendProgress');
    const response: any = await fn({ campaignId });
    const data = response?.data || {};
    return {
      exists: !!data.exists,
      status: data.status,
      totalRecipients: Number(data.totalRecipients || 0),
      sentCount: Number(data.sentCount || 0),
      failedCount: Number(data.failedCount || 0),
      skippedCount: Number(data.skippedCount || 0),
      shardCount: Number(data.shardCount || 0),
      shardsCompleted: Number(data.shardsCompleted || 0),
      deadLetterCount: Number(data.deadLetterCount || 0),
      error: data.error ?? null,
    };
  } catch (error) {
    console.error('Error fetching campaign send progress:', error);
    return {
      exists: false,
      totalRecipients: 0,
      sentCount: 0,
      failedCount: 0,
      skippedCount: 0,
      shardCount: 0,
      shardsCompleted: 0,
      deadLetterCount: 0,
    };
  }
};

/**
 * Retry recipients that exhausted their send attempts.
 *
 * Previously a permanently failed recipient was counted into `failureCount` and
 * never attempted again, with no record of who they were. They now land in
 * `campaign_dead_letters` and can be re-driven.
 */
export const retryCampaignDeadLetters = async (
  campaignId: string,
  limitCount?: number
): Promise<{ success: boolean; requeued: number; error?: string }> => {
  try {
    const fn = httpsCallable(functions, 'retryCampaignDeadLetters');
    const response: any = await fn({ campaignId, limit: limitCount });
    return { success: true, requeued: Number(response?.data?.requeued || 0) };
  } catch (error: any) {
    console.error('Error retrying campaign dead letters:', error);
    return { success: false, requeued: 0, error: error?.message || 'Re-drive failed' };
  }
};

export const scheduleCampaign = async (campaignId: string, scheduledAt: Date): Promise<void> => {
  try {
    await updateCampaign(campaignId, {
      status: 'scheduled',
      scheduledAt: Timestamp.fromDate(scheduledAt),
    });
  } catch (error) {
    console.error('Error scheduling campaign:', error);
    throw new Error('Failed to schedule campaign');
  }
};

// Schedule a campaign for multi-timezone sending
export const scheduleMultiTimezoneCampaign = async (
  campaignId: string,
  schedules: { timezone: string; scheduledAt: Date }[],
  targetDate: string,
  targetTime: string
): Promise<void> => {
  try {
    await updateCampaign(campaignId, {
      status: 'scheduled',
      scheduledAt: Timestamp.fromDate(schedules[0].scheduledAt), // Use first schedule as primary
      multiTimezoneSchedule: {
        targetDate,
        targetTime,
        schedules: schedules.map(schedule => ({
          timezone: schedule.timezone,
          scheduledAt: Timestamp.fromDate(schedule.scheduledAt)
        }))
      }
    });
  } catch (error) {
    console.error('Error scheduling multi-timezone campaign:', error);
    throw new Error('Failed to schedule multi-timezone campaign');
  }
};

// Pause a campaign
export const pauseCampaign = async (campaignId: string): Promise<void> => {
  try {
    await updateCampaign(campaignId, { status: 'paused' });
  } catch (error) {
    console.error('Error pausing campaign:', error);
    throw new Error('Failed to pause campaign');
  }
};

// Cancel a campaign
export const cancelCampaign = async (campaignId: string): Promise<void> => {
  try {
    await updateCampaign(campaignId, { status: 'cancelled' });
  } catch (error) {
    console.error('Error cancelling campaign:', error);
    throw new Error('Failed to cancel campaign');
  }
};

// Get campaign recipients
export const getCampaignRecipients = async (campaignId: string): Promise<CampaignRecipient[]> => {
  try {
    const q = query(
      collection(db, 'campaign_recipients'),
      where('campaignId', '==', campaignId)
    );

    const querySnapshot = await getDocs(q);
    const recipients = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as CampaignRecipient[];

    // Sort in memory instead of using orderBy
    return recipients
      .filter((recipient) => typeof recipient.email === 'string' && recipient.email.trim().length > 0)
      .sort((a, b) => a.email.localeCompare(b.email));
  } catch (error) {
    console.error('Error getting campaign recipients:', error);
    throw error;
  }
};

/** Fallback: derive sent recipients from email_logs when campaign_recipients is empty or unreadable. */
export const getCampaignSentRecipientsFromLogs = async (
  userId: string,
  campaignId: string
): Promise<CampaignRecipient[]> => {
  try {
    const q = query(
      collection(db, 'email_logs'),
      where('userId', '==', userId),
      where('campaignId', '==', campaignId)
    );
    const querySnapshot = await getDocs(q);
    const seen = new Set<string>();
    const recipients: CampaignRecipient[] = [];

    querySnapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const status = data.status === 'bounced' ? 'bounced' : 'sent';
      const rawTo = data.to;
      const emails: string[] = Array.isArray(rawTo)
        ? rawTo.filter((value): value is string => typeof value === 'string')
        : typeof rawTo === 'string'
          ? [rawTo]
          : [];

      emails.forEach((email) => {
        const trimmed = email.trim();
        if (!trimmed) return;
        const normalized = trimmed.toLowerCase();
        if (seen.has(normalized)) return;
        seen.add(normalized);
        recipients.push({
          id: `${docSnap.id}_${normalized}`,
          email: trimmed,
          customFields: {},
          status,
        });
      });
    });

    return recipients.sort((a, b) => a.email.localeCompare(b.email));
  } catch (error) {
    console.error('Error getting campaign recipients from email_logs:', error);
    return [];
  }
};

export const resolveCampaignRecipientsForEngagement = async (
  campaign: Campaign
): Promise<CampaignRecipient[]> => {
  if (!campaign.id) return [];

  try {
    const fromCollection = await getCampaignRecipients(campaign.id);
    if (fromCollection.length > 0) return fromCollection;
  } catch (error) {
    console.warn('resolveCampaignRecipientsForEngagement: campaign_recipients unavailable', error);
  }

  if (campaign.userId) {
    const fromLogs = await getCampaignSentRecipientsFromLogs(campaign.userId, campaign.id);
    if (fromLogs.length > 0) return fromLogs;
  }

  return [];
};

// Add recipients to campaign
export const addCampaignRecipients = async (campaignId: string, recipients: Omit<CampaignRecipient, 'id'>[]): Promise<void> => {
  try {
    console.log('Adding campaign recipients:', { campaignId, recipientCount: recipients.length });
    const batch = recipients.map(recipient =>
      addDoc(collection(db, 'campaign_recipients'), {
        ...recipient,
        campaignId,
        createdAt: Timestamp.now(),
      })
    );

    await Promise.all(batch);
    console.log('Campaign recipients added successfully');
  } catch (error) {
    console.error('Error adding campaign recipients:', error);
    throw new Error('Failed to add campaign recipients');
  }
};

// Update campaign recipient
export const updateCampaignRecipient = async (campaignId: string, recipientId: string, updates: Partial<CampaignRecipient>): Promise<void> => {
  try {
    const docRef = doc(db, 'campaign_recipients', recipientId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating campaign recipient:', error);
    throw new Error('Failed to update campaign recipient');
  }
};

export interface ContactCampaignHistoryItem {
  campaignId: string;
  campaignName: string;
  subject: string;
  campaignStatus: Campaign['status'];
  sentAt: Date | null;
  deliveryStatus: CampaignRecipient['status'];
  opened: boolean;
  clicked: boolean;
}

function recipientStatusRank(status: CampaignRecipient['status']): number {
  const ranks: Record<CampaignRecipient['status'], number> = {
    clicked: 6,
    opened: 5,
    delivered: 4,
    sent: 3,
    pending: 2,
    bounced: 1,
    complained: 1,
    unsubscribed: 1,
  };
  return ranks[status] ?? 0;
}

function parseFirestoreDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function contactEmailMatches(storedEmail: string | undefined, normalizedEmail: string): boolean {
  return (storedEmail || '').trim().toLowerCase() === normalizedEmail;
}

function isCampaignSentToContact(campaign: Campaign): boolean {
  if (campaign.status === 'sent') return true;
  return (campaign.statistics?.sent ?? 0) > 0;
}

async function findContactRecipientForCampaign(
  campaign: Campaign,
  userId: string,
  normalizedEmail: string,
  emailVariants: string[]
): Promise<CampaignRecipient | null> {
  if (!campaign.id) return null;

  for (const email of emailVariants) {
    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'campaign_recipients'),
          where('campaignId', '==', campaign.id),
          where('email', '==', email)
        )
      );
      if (!snapshot.empty) {
        const data = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as CampaignRecipient;
        return data;
      }
    } catch {
      // Composite index may be missing — fall through to broader lookups.
    }
  }

  try {
    const recipients = await getCampaignRecipients(campaign.id);
    const match = recipients.find((recipient) => contactEmailMatches(recipient.email, normalizedEmail));
    if (match) return match;
  } catch (error) {
    console.warn('getContactCampaignHistory: getCampaignRecipients failed', campaign.id, error);
  }

  const fromLogs = await getCampaignSentRecipientsFromLogs(userId, campaign.id);
  return fromLogs.find((recipient) => contactEmailMatches(recipient.email, normalizedEmail)) || null;
}

function recordEngagementEvent(
  engagementByCampaign: Map<string, { opened: boolean; clicked: boolean }>,
  campaignId: string,
  eventType: unknown
) {
  const current = engagementByCampaign.get(campaignId) || { opened: false, clicked: false };
  if (eventType === 'open') current.opened = true;
  if (eventType === 'click') current.clicked = true;
  engagementByCampaign.set(campaignId, current);
}

/** Campaigns this contact received, resolved from campaign_recipients and email_logs. */
export const getContactCampaignHistory = async (
  userId: string,
  workspaceId: string,
  contactEmail: string
): Promise<ContactCampaignHistoryItem[]> => {
  const trimmedEmail = contactEmail.trim();
  const normalizedEmail = trimmedEmail.toLowerCase();
  const emailVariants =
    trimmedEmail === normalizedEmail ? [trimmedEmail] : [trimmedEmail, normalizedEmail];

  const recipientByCampaign = new Map<string, CampaignRecipient>();
  const engagementByCampaign = new Map<string, { opened: boolean; clicked: boolean }>();

  const storeRecipient = (campaignId: string, recipient: CampaignRecipient) => {
    const existing = recipientByCampaign.get(campaignId);
    if (!existing || recipientStatusRank(recipient.status) > recipientStatusRank(existing.status)) {
      recipientByCampaign.set(campaignId, recipient);
    }
  };

  // Tracking events store recipientEmail lowercased.
  try {
    const trackingSnapshot = await getDocs(
      query(collection(db, 'email_tracking_events'), where('recipientEmail', '==', normalizedEmail))
    );
    trackingSnapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const campaignId = typeof data.campaignId === 'string' ? data.campaignId : null;
      if (!campaignId) return;
      if (data.workspaceId && data.workspaceId !== workspaceId) return;

      if (!recipientByCampaign.has(campaignId)) {
        storeRecipient(campaignId, {
          id: docSnap.id,
          email: trimmedEmail,
          customFields: {},
          status: 'sent',
        });
      }
      recordEngagementEvent(engagementByCampaign, campaignId, data.eventType);
    });
  } catch (error) {
    console.warn('getContactCampaignHistory: tracking events query failed', error);
  }

  for (const email of emailVariants) {
    try {
      const snapshot = await getDocs(
        query(collection(db, 'campaign_recipients'), where('email', '==', email))
      );
      snapshot.docs.forEach((docSnap) => {
        const data = { id: docSnap.id, ...docSnap.data() } as CampaignRecipient & {
          campaignId?: string;
          workspaceId?: string;
        };
        const campaignId = data.campaignId;
        if (!campaignId) return;
        if (data.workspaceId && data.workspaceId !== workspaceId) return;
        storeRecipient(campaignId, data);
      });
    } catch (error) {
      console.warn('getContactCampaignHistory: campaign_recipients email query failed', error);
    }
  }

  const campaigns = await getCampaigns(userId, workspaceId);
  const sentCampaigns = campaigns.filter(isCampaignSentToContact);

  await Promise.all(
    sentCampaigns.map(async (campaign) => {
      if (!campaign.id || recipientByCampaign.has(campaign.id)) return;
      const recipient = await findContactRecipientForCampaign(
        campaign,
        userId,
        normalizedEmail,
        emailVariants
      );
      if (recipient) {
        storeRecipient(campaign.id, recipient);
      }
    })
  );

  const campaignById = new Map(campaigns.map((campaign) => [campaign.id!, campaign]));
  const items: ContactCampaignHistoryItem[] = [];

  recipientByCampaign.forEach((recipient, campaignId) => {
    const campaign = campaignById.get(campaignId);
    if (!campaign || !isCampaignSentToContact(campaign)) return;

    const engagement = engagementByCampaign.get(campaignId);
    const opened =
      Boolean(engagement?.opened) ||
      recipient.status === 'opened' ||
      recipient.status === 'clicked' ||
      Boolean(recipient.openedAt);
    const clicked =
      Boolean(engagement?.clicked) ||
      recipient.status === 'clicked' ||
      Boolean(recipient.clickedAt);

    items.push({
      campaignId,
      campaignName: campaign.name,
      subject: campaign.subject,
      campaignStatus: campaign.status,
      sentAt: parseFirestoreDate(campaign.sentAt) || parseFirestoreDate(recipient.sentAt),
      deliveryStatus: recipient.status,
      opened,
      clicked,
    });
  });

  return items.sort((a, b) => {
    const aTime = a.sentAt?.getTime() ?? 0;
    const bTime = b.sentAt?.getTime() ?? 0;
    return bTime - aTime;
  });
};

// Get campaign statistics
export const getCampaignStatistics = async (campaignId: string): Promise<Campaign['statistics']> => {
  try {
    const campaign = await getCampaign(campaignId);
    if (!campaign) {
      throw new Error('Campaign not found');
    }
    return campaign.statistics;
  } catch (error) {
    console.error('Error getting campaign statistics:', error);
    throw new Error('Failed to get campaign statistics');
  }
};

export default {
  createCampaign,
  getCampaigns,
  getCampaign,
  updateCampaign,
  deleteCampaign,
  sendCampaign,
  scheduleCampaign,
  pauseCampaign,
  cancelCampaign,
  getCampaignRecipients,
  addCampaignRecipients,
  updateCampaignRecipient,
  getCampaignStatistics,
  getContactCampaignHistory,
  getDeletedCampaigns,
  restoreCampaign,
  permanentlyDeleteCampaign,
};
